import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Card } from 'primereact/card'
import { Stepper, StepperRefAttributes } from 'primereact/stepper'
import { StepperPanel } from 'primereact/stepperpanel'

import { EInvoiceDto } from '../../../../../data/models'
import { EInvoiceError, EInvoiceStatus } from '../../../data'
import { AdditionalData } from './additional-data'
import { AdditionalDocuments } from './additional-documents'
import { Summary } from './summary'
import { Validation } from './validation'
import { InvoiceVerification } from './verification/InvoiceVerification'

enum EInvoiceWizardStep {
  VALIDATION = 0,
  ADDITIONAL_DATA = 1,
  ADDITIONAL_DOCUMENTS = 2,
  VERIFICATION = 3,
  SUMMARY = 4,
}

const stepUrlMap = {
  validation: EInvoiceWizardStep.VALIDATION,
  'additional-data': EInvoiceWizardStep.ADDITIONAL_DATA,
  'additional-documents': EInvoiceWizardStep.ADDITIONAL_DOCUMENTS,
  verification: EInvoiceWizardStep.VERIFICATION,
  summary: EInvoiceWizardStep.SUMMARY,
}

const stepToUrlMap: Record<EInvoiceWizardStep, keyof typeof stepUrlMap> = {
  [EInvoiceWizardStep.VALIDATION]: 'validation',
  [EInvoiceWizardStep.ADDITIONAL_DATA]: 'additional-data',
  [EInvoiceWizardStep.ADDITIONAL_DOCUMENTS]: 'additional-documents',
  [EInvoiceWizardStep.VERIFICATION]: 'verification',
  [EInvoiceWizardStep.SUMMARY]: 'summary',
}

type Props = {
  eInvoice?: EInvoiceDto
  isLoading: boolean
  errors?: EInvoiceError
  isFirstUpload?: boolean
}

export const EInvoiceWizard: React.FC<Props> = ({
  eInvoice,
  isLoading,
  errors,
  isFirstUpload,
}) => {
  const { t } = useTranslation('eInvoiceDetails')
  const { id } = useParams<{ id: string }>()
  const { step: urlStep } = useParams<{ step?: string }>()
  const navigate = useNavigate()
  const stepperRef = useRef<StepperRefAttributes | null>(null)
  const hasInitialized = useRef(false)
  const isNavigatingFromUrl = useRef(false)
  const lastStepperStep = useRef<number | null | undefined>(null)

  const [activeStep, setActiveStep] = useState<EInvoiceWizardStep | null>(null)
  const [verificationError, setVerificationError] = useState<EInvoiceError>()

  const hasErrors =
    errors?.Status === EInvoiceStatus.Error ||
    errors?.Results?.some(result => result.Level === 'Error')

  // Handle URL step parameter
  useEffect(() => {
    if (
      urlStep &&
      stepUrlMap[urlStep as keyof typeof stepUrlMap] !== undefined
    ) {
      const stepFromUrl = stepUrlMap[urlStep as keyof typeof stepUrlMap]
      isNavigatingFromUrl.current = true
      setActiveStep(stepFromUrl)
      hasInitialized.current = true
    }
  }, [urlStep])

  // Determine the active step based on the eInvoice status - only on initial load if no URL step
  useEffect(() => {
    // Skip if already initialized from URL or still loading
    if (hasInitialized.current || isLoading) return

    // Wait for data to be loaded before determining initial step
    if (!eInvoice && !isFirstUpload && !hasErrors) return

    hasInitialized.current = true

    let initialStep: EInvoiceWizardStep
    if (isFirstUpload || hasErrors || !eInvoice) {
      initialStep = EInvoiceWizardStep.VALIDATION
    } else if (eInvoice.Meta?.file.status === 'UnverifiedInvoice') {
      initialStep = EInvoiceWizardStep.ADDITIONAL_DATA
    } else if (eInvoice.Meta?.file.status === 'FailedRulesEngineCheck') {
      initialStep = EInvoiceWizardStep.VERIFICATION
    } else if (
      eInvoice.ValidationResult?.Results &&
      eInvoice.ValidationResult?.Results?.some(
        result =>
          result.ValidationType === 'RulesEngine' &&
          (result.Level === 'Error' || result.Level === 'Warn'),
      )
    ) {
      initialStep = EInvoiceWizardStep.VERIFICATION
    } else {
      initialStep = EInvoiceWizardStep.SUMMARY
    }

    setActiveStep(initialStep)
    // Navigate to the initial step URL
    if (id) {
      navigate(`/e-invoices/${id}/${stepToUrlMap[initialStep]}`, {
        replace: true,
      })
    }
  }, [eInvoice, isFirstUpload, hasErrors, isLoading, id, navigate])

  // Update the stepper via ref when the activeStep changes
  useEffect(() => {
    if (stepperRef.current && activeStep !== null) {
      stepperRef.current.setActiveStep(activeStep)
      lastStepperStep.current = activeStep
    }
  }, [activeStep])

  // Poll for stepper state changes
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (stepperRef.current && id) {
        const currentStep = stepperRef.current.getActiveStep()
        if (currentStep !== null && currentStep !== lastStepperStep.current) {
          const newStep = currentStep as EInvoiceWizardStep
          lastStepperStep.current = currentStep
          navigate(`/e-invoices/${id}/${stepToUrlMap[newStep]}`)
        }
      }
    }, 100)

    return () => clearInterval(pollInterval)
  }, [id, navigate])

  return (
    <Card>
      <Stepper ref={stepperRef} linear>
        <StepperPanel header={t('stepper.validation.title')}>
          <Validation
            eInvoice={eInvoice}
            errors={errors}
            stepperRef={stepperRef}
          />
        </StepperPanel>
        <StepperPanel header={t('stepper.additionalData.title')}>
          <AdditionalData
            eInvoice={eInvoice}
            isLoading={isLoading}
            stepperRef={stepperRef}
          />
        </StepperPanel>
        <StepperPanel header={t('stepper.additionalDocuments.title')}>
          <AdditionalDocuments
            eInvoice={eInvoice}
            stepperRef={stepperRef}
            setVerificationError={setVerificationError}
          />
        </StepperPanel>
        <StepperPanel header={t('stepper.verification.title')}>
          <InvoiceVerification
            eInvoice={eInvoice}
            error={verificationError}
            stepperRef={stepperRef}
          />
        </StepperPanel>

        <StepperPanel header={t('stepper.summary.title')}>
          <Summary eInvoice={eInvoice} stepperRef={stepperRef} />
        </StepperPanel>
      </Stepper>
    </Card>
  )
}
